import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar, Trophy, Clock, Users, DollarSign, AlertCircle, Plus } from "lucide-react";
import type { Registration } from "@/pages/hub/HubTournamentDemo";

interface Tournament {
  id: string;
  name: string;
  dates: string;
  format: string;
  registrationDeadline: string;
  depositDeadline: string;
  fees: Record<string, { amount: number; surface: string }>;
  depositAmount: number;
  expectedTeams: number;
}

interface Props {
  tournament: Tournament;
  registrations: Registration[];
}

function daysUntil(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function AdminViewTab({ tournament, registrations }: Props) {
  const [dialogOpen, setDialogOpen] = useState(false);

  const totalCollected = registrations.reduce((sum, r) => sum + r.paid, 0);
  const totalOutstanding = registrations.reduce(
    (sum, r) => sum + (r.amountDue - r.paid),
    0
  );
  const daysToDeposit = daysUntil(tournament.depositDeadline);
  const registeredCount = registrations.length;
  const progressPct = Math.min(
    100,
    Math.round((registeredCount / tournament.expectedTeams) * 100)
  );

  return (
    <div className="space-y-6" data-testid="admin-view-tab">
      {/* Tournament hero */}
      <Card className="border-0 bg-gradient-to-br from-[#1E2761] to-[#2d3a8c] text-white shadow-lg">
        <CardContent className="pt-6 pb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-blue-200 text-sm">
                <Trophy className="h-4 w-4" />
                <span>{tournament.format}</span>
              </div>
              <h2 className="text-2xl font-bold">{tournament.name}</h2>
              <div className="flex flex-wrap gap-4 text-sm text-blue-200 mt-2">
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {tournament.dates}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Registration closes {formatDate(tournament.registrationDeadline)}
                </span>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-3xl font-bold">{registeredCount}</p>
              <p className="text-blue-200 text-sm">
                of {tournament.expectedTeams} expected teams
              </p>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex justify-between text-xs text-blue-200 mb-1">
              <span>Registration progress</span>
              <span>{progressPct}%</span>
            </div>
            <Progress value={progressPct} className="h-2 bg-blue-900 [&>div]:bg-white" />
          </div>
        </CardContent>
      </Card>

      {/* 4-stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Teams Registered</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{registeredCount}</p>
              </div>
              <Users className="h-5 w-5 text-blue-500 mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Collected</p>
                <p className="text-2xl font-bold text-green-700 mt-1">
                  ${totalCollected.toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-5 w-5 text-green-500 mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Outstanding</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  ${totalOutstanding.toLocaleString()}
                </p>
              </div>
              <AlertCircle className="h-5 w-5 text-red-400 mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5 pb-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Deposit Deadline</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{daysToDeposit}d</p>
              </div>
              <Clock className="h-5 w-5 text-amber-400 mt-1" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fee schedule + Create button */}
      <div className="flex flex-col md:flex-row gap-4">
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Tournament Fees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(tournament.fees).map(([ageGroup, fee]) => (
                <div
                  key={ageGroup}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="w-10 justify-center">
                      {ageGroup}
                    </Badge>
                    <span className="text-sm text-gray-600">{fee.surface}</span>
                  </div>
                  <span className="font-semibold text-gray-900">${fee.amount}</span>
                </div>
              ))}
              <div className="pt-2 flex items-center justify-between text-sm text-gray-500">
                <span>Deposit (non-refundable, by {formatDate(tournament.depositDeadline)})</span>
                <span className="font-medium text-gray-700">${tournament.depositAmount}</span>
              </div>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>Full payment by</span>
                <span className="font-medium text-gray-700">
                  {formatDate(tournament.registrationDeadline)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="md:w-56 flex flex-col justify-start">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="w-full bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
                data-testid="btn-create-tournament"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create New Tournament
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Tournament</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div>
                  <Label htmlFor="t-name">Tournament Name</Label>
                  <Input id="t-name" placeholder="e.g. Fall Classic 2026" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="t-start">Start Date</Label>
                    <Input id="t-start" type="date" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="t-end">End Date</Label>
                    <Input id="t-end" type="date" className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="t-format">Format</Label>
                  <Input id="t-format" placeholder="e.g. 30 Overs" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="t-teams">Expected Teams</Label>
                  <Input id="t-teams" type="number" placeholder="32" className="mt-1" />
                </div>
                <p className="text-xs text-gray-400 text-center pt-2">
                  Visual demo only — form does not submit
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
